
import { AlertTriangle, Trash2, X, Loader2 } from "lucide-react";

export interface DeleteConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    itemName?: string;
    itemType?: string;
    busy?: boolean;
}

export default function DeleteConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    itemName = "",
    itemType = "item",
    busy = false,
}: DeleteConfirmModalProps) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-red-500/10 rounded">
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                        </div>
                        <h3 className="text-base font-bold text-white leading-normal capitalize">
                            Delete {itemType}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 flex flex-col items-center text-center gap-4">
                    <div className="p-4 bg-red-500/10 rounded-full">
                        <Trash2 className="w-8 h-8 text-red-500" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <h4 className="text-sm font-bold text-white uppercase tracking-wider">Are you absolutely sure?</h4>
                        <p className="text-xs text-gray-400 max-w-sm leading-relaxed">
                            This action cannot be undone. You are about to permanently delete the {itemType}: <br />
                            <span className="font-bold text-white font-mono text-[11px] bg-gray-950 px-2 py-1 rounded inline-block mt-2 border border-gray-800">
                                {itemName}
                            </span>
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-4 border-t border-gray-800 bg-gray-800/50 flex items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="px-4 py-2 text-xs font-bold text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={busy}
                        className="flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors min-w-[100px] disabled:opacity-50"
                    >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : "Delete"}
                    </button>
                </div>
            </div>
        </div>
    );
}
